import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import type {
  Certification,
  Clearance,
  ClearanceLevel,
  CreateCertificationInput,
  CreateClearanceInput,
  CreateFirearmsQualificationInput,
  CreateLicenseInput,
  CreatePersonnelAvailabilityInput,
  CreateTrainingInput,
  FirearmsQualification,
  License,
  LicenseStatus,
  LicenseType,
  PersonnelAvailability,
  Training,
  WeaponClass,
} from '@dsr/shared-types';
import {
  usePerson,
  usePersonHistory,
  useRevealPersonGovernmentId,
} from '../api/queries/persons';
import { PersonDetailView } from '../components/PersonDetailView';
import {
  usePersonnel,
  useLicenses,
  useCreateLicense,
  useDeleteLicense,
  useCertifications,
  useCreateCertification,
  useDeleteCertification,
  useFirearmsQualifications,
  useCreateFirearmsQualification,
  useDeleteFirearmsQualification,
  useClearances,
  useCreateClearance,
  useDeleteClearance,
  useTrainings,
  useCreateTraining,
  useDeleteTraining,
  useAvailability,
  useCreateAvailability,
  useDeleteAvailability,
} from '../api/queries/personnel';
import { DateInput } from '../components/DateInput';
import { MailboxCard } from '../components/MailboxCard';
import {
  Button,
  ConfirmDialog,
  Dialog,
  FormField,
  Input,
  StatusBadge,
  Table,
  Tabs,
  type TabItem,
} from '../components/ui';
import { ApiError } from '../api/client';
import { can } from '../auth/store';
import { toast } from '../components/Toasts';

// ---------- Formatting helpers ----------

function fmtDate(epoch: number | null | undefined): string {
  if (epoch == null) return '—';
  const d = new Date(epoch);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** Tone for an expiry date: danger if past/<=30d, warn if <=60d, else default. */
function expiryTone(epoch: number | null | undefined): 'default' | 'warn' | 'danger' {
  if (epoch == null) return 'default';
  const days = (epoch - Date.now()) / DAY_MS;
  if (days <= 30) return 'danger';
  if (days <= 60) return 'warn';
  return 'default';
}

function fmtMinutes(min: number | null | undefined): string {
  if (min == null) return '—';
  const h = Math.floor(min / 60);
  const m = min % 60;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}

// ---------- Enum option tables ----------

const LICENSE_TYPE_OPTIONS: { value: LicenseType; label: string }[] = [
  { value: 'guard_card', label: 'Guard Card' },
  { value: 'armed_permit', label: 'Armed Permit' },
  { value: 'ccw', label: 'CCW' },
  { value: 'driver', label: 'Driver' },
  { value: 'medic_emr', label: 'Medic — EMR' },
  { value: 'medic_emt', label: 'Medic — EMT' },
  { value: 'private_investigator', label: 'Private Investigator' },
  { value: 'security_company', label: 'Security Company' },
  { value: 'other', label: 'Other' },
];

const LICENSE_STATUS_OPTIONS: { value: LicenseStatus; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'expired', label: 'Expired' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'pending', label: 'Pending' },
];

const WEAPON_CLASS_OPTIONS: { value: WeaponClass; label: string }[] = [
  { value: 'handgun', label: 'Handgun' },
  { value: 'rifle', label: 'Rifle' },
  { value: 'shotgun', label: 'Shotgun' },
  { value: 'less_lethal', label: 'Less-Lethal' },
];

const EMPLOYMENT_TYPE_LABEL: Record<string, string> = {
  full_time: 'Full-time',
  part_time: 'Part-time',
  contractor: 'Contractor',
  temp: 'Temporary',
};

const CLEARANCE_LEVEL_OPTIONS: { value: ClearanceLevel; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'basic_bgc', label: 'Basic BGC' },
  { value: 'enhanced_bgc', label: 'Enhanced BGC' },
  { value: 'secret', label: 'Secret' },
  { value: 'top_secret', label: 'Top Secret' },
];

function labelFor<T extends string>(
  options: { value: T; label: string }[],
  value: T | null | undefined,
): string {
  if (value == null) return '—';
  return options.find((o) => o.value === value)?.label ?? value;
}

export function PersonnelDetailPage(): JSX.Element {
  const params = useParams();
  const id = Number(params['id']);
  const valid = Number.isInteger(id) && id > 0;

  const { data, isLoading, isError, error } = usePersonnel(valid ? id : null);
  const person = data?.personnel;

  // Firearms tab is hidden entirely unless the viewer can read the sensitive
  // qualification records.
  const canReadFirearms = can('firearm.read');

  if (!valid) {
    return <div className="page-state page-error">Invalid personnel id.</div>;
  }
  if (isLoading) {
    return <div className="page-state">Loading personnel…</div>;
  }
  if (isError || !person) {
    return (
      <div className="page-state page-error" role="alert">
        {error instanceof ApiError ? error.message : 'Personnel not found.'}
      </div>
    );
  }

  const tabItems: TabItem[] = [
    { value: 'profile', label: 'Profile', content: <ProfileTab person={person} /> },
    { value: 'licenses', label: 'Licenses', content: <LicensesTab personnelId={id} /> },
    { value: 'certifications', label: 'Certifications', content: <CertificationsTab personnelId={id} /> },
    ...(canReadFirearms
      ? [{ value: 'firearms', label: 'Firearms', content: <FirearmsTab personnelId={id} /> }]
      : []),
    { value: 'clearances', label: 'Clearances', content: <ClearancesTab personnelId={id} /> },
    { value: 'trainings', label: 'Training', content: <TrainingsTab personnelId={id} /> },
    { value: 'availability', label: 'Availability', content: <AvailabilityTab personnelId={id} /> },
  ];

  return (
    <div className="page personnel-detail-page">
      <header className="page-header">
        <div>
          <div className="breadcrumb">
            <Link to="/personnel" className="table-link">
              Personnel
            </Link>
            <span aria-hidden="true"> / </span>
            <span>{person.legal_name}</span>
          </div>
          <h1>{person.legal_name}</h1>
          <p className="page-sub">
            {person.employee_no ? `Employee #${person.employee_no} · ` : ''}
            <span className="capitalize">{person.status}</span>
          </p>
        </div>
      </header>

      <Tabs items={tabItems} defaultValue="profile" />
    </div>
  );
}

// =============================================================================
// Profile tab
// =============================================================================

function ProfileTab({ person }: { person: NonNullable<ReturnType<typeof usePersonnel>['data']>['personnel'] }): JSX.Element {
  const ec = person.emergency_contact;

  // Load linked person data to derive hasPrimaryPersonalEmail for MailboxCard.
  // TanStack Query shares the cache with PersonProfileCard below — no extra
  // network round-trip when both render on the same page.
  const linkedPersonId =
    person.person_id != null && person.person_id > 0 ? person.person_id : null;
  const { data: linkedPersonData } = usePerson(linkedPersonId);
  const hasPrimaryPersonalEmail =
    linkedPersonData?.person.emails?.some(
      (e) => e.is_primary && !e.is_company_managed && (e.label === 'personal' || e.label === 'other'),
    ) ?? false;

  return (
    <>
      <div className="detail-card">
        <dl className="detail-grid">
          <div>
            <dt>Legal name</dt>
            <dd>{person.legal_name}</dd>
          </div>
          <div>
            <dt>Employee #</dt>
            <dd>{person.employee_no ?? '—'}</dd>
          </div>
          <div>
            <dt>Status</dt>
            <dd className="capitalize">{person.status}</dd>
          </div>
          <div>
            <dt>Classifications</dt>
            <dd>
              {person.classifications.length === 0
                ? '—'
                : person.classifications.map((c) => c.replace(/_/g, ' ')).join(', ')}
            </dd>
          </div>
          <div>
            <dt>Linked user</dt>
            <dd>{person.user_id == null ? '—' : `#${person.user_id}`}</dd>
          </div>
          <div>
            <dt>Home location</dt>
            <dd>
              {person.home_lat && person.home_lng
                ? `${person.home_lat}, ${person.home_lng}`
                : '—'}
            </dd>
          </div>
          <div>
            <dt>Emergency contact</dt>
            <dd>
              {ec
                ? [ec.name, ec.relationship, ec.phone, ec.email].filter(Boolean).join(' · ') || '—'
                : '—'}
            </dd>
          </div>
        </dl>
      </div>

      {/* HR fields (0021) */}
      <div className="detail-card">
        <h2>Employment</h2>
        <dl className="detail-grid">
          <div>
            <dt>Position</dt>
            <dd>{person.position_title ?? '—'}</dd>
          </div>
          <div>
            <dt>Department</dt>
            <dd>{person.department ?? '—'}</dd>
          </div>
          <div>
            <dt>Employment type</dt>
            <dd>{EMPLOYMENT_TYPE_LABEL[person.employment_type] ?? person.employment_type}</dd>
          </div>
          <div>
            <dt>Hire date</dt>
            <dd>{fmtDate(person.hire_date)}</dd>
          </div>
          <div>
            <dt>Termination date</dt>
            <dd>{fmtDate(person.termination_date)}</dd>
          </div>
          <div>
            <dt>Supervisor</dt>
            <dd>
              {person.supervisor_personnel_id == null ? (
                '—'
              ) : (
                <Link
                  to={`/personnel/${person.supervisor_personnel_id}`}
                  className="table-link"
                >
                  #{person.supervisor_personnel_id}
                </Link>
              )}
            </dd>
          </div>
        </dl>
      </div>

      {/* Person registry profile (linked identity) */}
      {person.person_id != null && person.person_id > 0 && (
        <PersonProfileCard personId={person.person_id} />
      )}

      {/* Company mailbox card — only meaningful when there is a linked person */}
      {linkedPersonId !== null && (
        <MailboxCard
          personId={linkedPersonId}
          hasPrimaryPersonalEmail={hasPrimaryPersonalEmail}
        />
      )}
    </>
  );
}

// Linked person-registry identity rendered via the shared PersonDetailView.
// History/reveal are gated by can() (see the component's internal gating) and
// only fetched when the viewer holds the matching permission.
function PersonProfileCard({ personId }: { personId: number }): JSX.Element {
  const { data, isLoading, isError } = usePerson(personId);
  const canHistory = can('person.read_history');
  const canGovIds = can('person.read_government_ids');
  const { data: historyData } = usePersonHistory(personId, canHistory);
  const reveal = useRevealPersonGovernmentId();

  const onRevealGovernmentId = canGovIds
    ? async (govId: number): Promise<string> => {
        const res = await reveal.mutateAsync({ personId, govId });
        return res.number;
      }
    : undefined;

  return (
    <section className="detail-card">
      <h2>Person profile</h2>
      {isLoading && <p className="muted">Loading person profile…</p>}
      {isError && <p className="muted">Failed to load the linked person profile.</p>}
      {!isLoading && !isError && data?.person && (
        <PersonDetailView
          person={data.person}
          history={historyData?.history ?? null}
          onRevealGovernmentId={onRevealGovernmentId}
        />
      )}
    </section>
  );
}

// =============================================================================
// Generic credential-section scaffolding
// =============================================================================

interface SectionFrameProps {
  title: string;
  canWrite: boolean;
  onAdd: () => void;
  addLabel: string;
  /** Sensitive sections render a small banner explaining the gating. */
  sensitive?: boolean;
  children: React.ReactNode;
}

function SectionFrame({
  title,
  canWrite,
  onAdd,
  addLabel,
  sensitive = false,
  children,
}: SectionFrameProps): JSX.Element {
  return (
    <section className="credential-section">
      <div className="section-header">
        <h2>
          {title}
          {sensitive && (
            <StatusBadge status="danger" className="section-sensitive">
              Sensitive
            </StatusBadge>
          )}
        </h2>
        {canWrite && (
          <Button variant="primary" className="btn-sm" onClick={onAdd}>
            {addLabel}
          </Button>
        )}
      </div>
      {children}
    </section>
  );
}

interface RowActionsProps {
  canWrite: boolean;
  onDelete: () => void;
}

function RowActions({ canWrite, onDelete }: RowActionsProps): JSX.Element | null {
  if (!canWrite) return null;
  return (
    <Button variant="ghost" className="btn-sm btn-danger-text" onClick={onDelete}>
      Remove
    </Button>
  );
}

function ExpiryCell({ epoch }: { epoch: number | null }): JSX.Element {
  const tone = expiryTone(epoch);
  return <StatusBadge status={tone}>{fmtDate(epoch)}</StatusBadge>;
}

// =============================================================================
// Licenses tab (credential.read / credential.write)
// =============================================================================

function LicensesTab({ personnelId }: { personnelId: number }): JSX.Element {
  const canWrite = can('credential.write');
  const { data, isLoading, isError } = useLicenses(personnelId, can('credential.read'));
  const create = useCreateLicense();
  const del = useDeleteLicense();
  const [addOpen, setAddOpen] = useState(false);
  const [confirmId, setConfirmId] = useState<number | null>(null);

  const items = data?.licenses ?? [];

  return (
    <SectionFrame
      title="Licenses"
      canWrite={canWrite}
      onAdd={() => setAddOpen(true)}
      addLabel="Add License"
    >
      {isLoading && <div className="page-state">Loading…</div>}
      {isError && <div className="page-state page-error">Failed to load licenses.</div>}
      {!isLoading && !isError && (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>Type</Table.HeaderCell>
              <Table.HeaderCell>Number</Table.HeaderCell>
              <Table.HeaderCell>Authority / State</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell>Expires</Table.HeaderCell>
              <Table.HeaderCell />
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {items.length === 0 && (
              <Table.Empty colSpan={6}>No licenses on file.</Table.Empty>
            )}
            {items.map((l: License) => (
              <Table.Row key={l.id}>
                <Table.Cell>{labelFor(LICENSE_TYPE_OPTIONS, l.license_type)}</Table.Cell>
                <Table.Cell>{l.number ?? '—'}</Table.Cell>
                <Table.Cell>
                  {[l.issuing_authority, l.state].filter(Boolean).join(' · ') || '—'}
                </Table.Cell>
                <Table.Cell className="capitalize">{l.status}</Table.Cell>
                <Table.Cell>
                  <ExpiryCell epoch={l.expires_at} />
                </Table.Cell>
                <Table.Cell className="table-actions">
                  <RowActions canWrite={canWrite} onDelete={() => setConfirmId(l.id)} />
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      {addOpen && (
        <LicenseFormModal
          personnelId={personnelId}
          open={addOpen}
          onClose={() => setAddOpen(false)}
          create={create}
        />
      )}

      <ConfirmDialog
        open={confirmId !== null}
        title="Remove license"
        message="This license will be removed from the personnel record."
        confirmLabel="Remove"
        danger
        busy={del.isPending}
        onCancel={() => setConfirmId(null)}
        onConfirm={() => {
          if (confirmId == null) return;
          del.mutate(
            { personnelId, childId: confirmId },
            {
              onSuccess: () => {
                toast.success('License removed.');
                setConfirmId(null);
              },
              onError: () => toast.danger('Failed to remove license.'),
            },
          );
        }}
      />
    </SectionFrame>
  );
}

function LicenseFormModal({
  personnelId,
  open,
  onClose,
  create,
}: {
  personnelId: number;
  open: boolean;
  onClose: () => void;
  create: ReturnType<typeof useCreateLicense>;
}): JSX.Element {
  const [licenseType, setLicenseType] = useState<LicenseType>('guard_card');
  const [number, setNumber] = useState('');
  const [authority, setAuthority] = useState('');
  const [state, setState] = useState('');
  const [status, setStatus] = useState<LicenseStatus>('active');
  const [issuedAt, setIssuedAt] = useState<number | null>(null);
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    setFormError(null);
    const input: CreateLicenseInput = {
      license_type: licenseType,
      status,
      ...(number.trim() ? { number: number.trim() } : {}),
      ...(authority.trim() ? { issuing_authority: authority.trim() } : {}),
      ...(state.trim() ? { state: state.trim() } : {}),
      ...(issuedAt != null ? { issued_at: issuedAt } : {}),
      ...(expiresAt != null ? { expires_at: expiresAt } : {}),
    };
    create.mutate(
      { personnelId, input },
      {
        onSuccess: () => {
          toast.success('License added.');
          onClose();
        },
        onError: (err) =>
          setFormError(err instanceof ApiError ? err.message : 'Failed to add license.'),
      },
    );
  };

  return (
    <CredentialModal
      title="Add License"
      open={open}
      onClose={onClose}
      formId="license-form"
      busy={create.isPending}
    >
      <form id="license-form" onSubmit={onSubmit} className="form-grid">
        <FormField label="Type">
          <select
            className="form-input"
            value={licenseType}
            onChange={(e) => setLicenseType(e.target.value as LicenseType)}
          >
            {LICENSE_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="Number">
          <Input value={number} onChange={(e) => setNumber(e.target.value)} />
        </FormField>
        <FormField label="Issuing authority">
          <Input value={authority} onChange={(e) => setAuthority(e.target.value)} />
        </FormField>
        <FormField label="State">
          <Input value={state} onChange={(e) => setState(e.target.value)} />
        </FormField>
        <FormField label="Status">
          <select
            className="form-input"
            value={status}
            onChange={(e) => setStatus(e.target.value as LicenseStatus)}
          >
            {LICENSE_STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="Issued">
          <DateInput value={issuedAt} onChange={setIssuedAt} />
        </FormField>
        <FormField label="Expires">
          <DateInput value={expiresAt} onChange={setExpiresAt} />
        </FormField>
        {formError && (
          <p className="form-error" role="alert">
            {formError}
          </p>
        )}
      </form>
    </CredentialModal>
  );
}

// =============================================================================
// Certifications tab (credential.read / credential.write)
// =============================================================================

function CertificationsTab({ personnelId }: { personnelId: number }): JSX.Element {
  const canWrite = can('credential.write');
  const { data, isLoading, isError } = useCertifications(personnelId, can('credential.read'));
  const create = useCreateCertification();
  const del = useDeleteCertification();
  const [addOpen, setAddOpen] = useState(false);
  const [confirmId, setConfirmId] = useState<number | null>(null);

  const items = data?.certifications ?? [];

  return (
    <SectionFrame
      title="Certifications"
      canWrite={canWrite}
      onAdd={() => setAddOpen(true)}
      addLabel="Add Certification"
    >
      {isLoading && <div className="page-state">Loading…</div>}
      {isError && <div className="page-state page-error">Failed to load certifications.</div>}
      {!isLoading && !isError && (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>Name</Table.HeaderCell>
              <Table.HeaderCell>Issuer</Table.HeaderCell>
              <Table.HeaderCell>Issued</Table.HeaderCell>
              <Table.HeaderCell>Expires</Table.HeaderCell>
              <Table.HeaderCell />
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {items.length === 0 && (
              <Table.Empty colSpan={5}>No certifications on file.</Table.Empty>
            )}
            {items.map((c: Certification) => (
              <Table.Row key={c.id}>
                <Table.Cell>{c.name}</Table.Cell>
                <Table.Cell>{c.issuer ?? '—'}</Table.Cell>
                <Table.Cell>{fmtDate(c.issued_at)}</Table.Cell>
                <Table.Cell>
                  <ExpiryCell epoch={c.expires_at} />
                </Table.Cell>
                <Table.Cell className="table-actions">
                  <RowActions canWrite={canWrite} onDelete={() => setConfirmId(c.id)} />
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      {addOpen && (
        <CertificationFormModal
          personnelId={personnelId}
          open={addOpen}
          onClose={() => setAddOpen(false)}
          create={create}
        />
      )}

      <ConfirmDialog
        open={confirmId !== null}
        title="Remove certification"
        message="This certification will be removed from the personnel record."
        confirmLabel="Remove"
        danger
        busy={del.isPending}
        onCancel={() => setConfirmId(null)}
        onConfirm={() => {
          if (confirmId == null) return;
          del.mutate(
            { personnelId, childId: confirmId },
            {
              onSuccess: () => {
                toast.success('Certification removed.');
                setConfirmId(null);
              },
              onError: () => toast.danger('Failed to remove certification.'),
            },
          );
        }}
      />
    </SectionFrame>
  );
}

function CertificationFormModal({
  personnelId,
  open,
  onClose,
  create,
}: {
  personnelId: number;
  open: boolean;
  onClose: () => void;
  create: ReturnType<typeof useCreateCertification>;
}): JSX.Element {
  const [name, setName] = useState('');
  const [issuer, setIssuer] = useState('');
  const [issuedAt, setIssuedAt] = useState<number | null>(null);
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    setFormError(null);
    if (!name.trim()) {
      setFormError('Name is required.');
      return;
    }
    const input: CreateCertificationInput = {
      name: name.trim(),
      ...(issuer.trim() ? { issuer: issuer.trim() } : {}),
      ...(issuedAt != null ? { issued_at: issuedAt } : {}),
      ...(expiresAt != null ? { expires_at: expiresAt } : {}),
    };
    create.mutate(
      { personnelId, input },
      {
        onSuccess: () => {
          toast.success('Certification added.');
          onClose();
        },
        onError: (err) =>
          setFormError(err instanceof ApiError ? err.message : 'Failed to add certification.'),
      },
    );
  };

  return (
    <CredentialModal
      title="Add Certification"
      open={open}
      onClose={onClose}
      formId="cert-form"
      busy={create.isPending}
    >
      <form id="cert-form" onSubmit={onSubmit} className="form-grid">
        <FormField label="Name" required>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
          />
        </FormField>
        <FormField label="Issuer">
          <Input value={issuer} onChange={(e) => setIssuer(e.target.value)} />
        </FormField>
        <FormField label="Issued">
          <DateInput value={issuedAt} onChange={setIssuedAt} />
        </FormField>
        <FormField label="Expires">
          <DateInput value={expiresAt} onChange={setExpiresAt} />
        </FormField>
        {formError && (
          <p className="form-error" role="alert">
            {formError}
          </p>
        )}
      </form>
    </CredentialModal>
  );
}

// =============================================================================
// Firearms qualifications tab (SENSITIVE: firearm.read / firearm.write)
// =============================================================================

function FirearmsTab({ personnelId }: { personnelId: number }): JSX.Element {
  const canWrite = can('firearm.write');
  const { data, isLoading, isError } = useFirearmsQualifications(personnelId, can('firearm.read'));
  const create = useCreateFirearmsQualification();
  const del = useDeleteFirearmsQualification();
  const [addOpen, setAddOpen] = useState(false);
  const [confirmId, setConfirmId] = useState<number | null>(null);

  const items = data?.firearms_qualifications ?? [];

  return (
    <SectionFrame
      title="Firearms Qualifications"
      canWrite={canWrite}
      onAdd={() => setAddOpen(true)}
      addLabel="Add Qualification"
      sensitive
    >
      {isLoading && <div className="page-state">Loading…</div>}
      {isError && (
        <div className="page-state page-error">Failed to load firearms qualifications.</div>
      )}
      {!isLoading && !isError && (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>Weapon class</Table.HeaderCell>
              <Table.HeaderCell>Score</Table.HeaderCell>
              <Table.HeaderCell>Qualified</Table.HeaderCell>
              <Table.HeaderCell>Expires</Table.HeaderCell>
              <Table.HeaderCell>Instructor</Table.HeaderCell>
              <Table.HeaderCell />
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {items.length === 0 && (
              <Table.Empty colSpan={6}>No firearms qualifications on file.</Table.Empty>
            )}
            {items.map((f: FirearmsQualification) => (
              <Table.Row key={f.id}>
                <Table.Cell>{labelFor(WEAPON_CLASS_OPTIONS, f.weapon_class)}</Table.Cell>
                <Table.Cell>{f.score == null ? '—' : f.score}</Table.Cell>
                <Table.Cell>{fmtDate(f.qualified_at)}</Table.Cell>
                <Table.Cell>
                  <ExpiryCell epoch={f.expires_at} />
                </Table.Cell>
                <Table.Cell>{f.instructor ?? '—'}</Table.Cell>
                <Table.Cell className="table-actions">
                  <RowActions canWrite={canWrite} onDelete={() => setConfirmId(f.id)} />
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      {addOpen && (
        <FirearmsFormModal
          personnelId={personnelId}
          open={addOpen}
          onClose={() => setAddOpen(false)}
          create={create}
        />
      )}

      <ConfirmDialog
        open={confirmId !== null}
        title="Remove qualification"
        message="This firearms qualification will be removed from the personnel record."
        confirmLabel="Remove"
        danger
        busy={del.isPending}
        onCancel={() => setConfirmId(null)}
        onConfirm={() => {
          if (confirmId == null) return;
          del.mutate(
            { personnelId, childId: confirmId },
            {
              onSuccess: () => {
                toast.success('Qualification removed.');
                setConfirmId(null);
              },
              onError: () => toast.danger('Failed to remove qualification.'),
            },
          );
        }}
      />
    </SectionFrame>
  );
}

function FirearmsFormModal({
  personnelId,
  open,
  onClose,
  create,
}: {
  personnelId: number;
  open: boolean;
  onClose: () => void;
  create: ReturnType<typeof useCreateFirearmsQualification>;
}): JSX.Element {
  const [weaponClass, setWeaponClass] = useState<WeaponClass>('handgun');
  const [score, setScore] = useState('');
  const [qualifiedAt, setQualifiedAt] = useState<number | null>(null);
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [instructor, setInstructor] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    setFormError(null);
    let scoreNum: number | undefined;
    if (score.trim()) {
      const n = Number(score);
      if (!Number.isInteger(n) || n < 0 || n > 100) {
        setFormError('Score must be a whole number from 0 to 100.');
        return;
      }
      scoreNum = n;
    }
    const input: CreateFirearmsQualificationInput = {
      weapon_class: weaponClass,
      ...(scoreNum != null ? { score: scoreNum } : {}),
      ...(qualifiedAt != null ? { qualified_at: qualifiedAt } : {}),
      ...(expiresAt != null ? { expires_at: expiresAt } : {}),
      ...(instructor.trim() ? { instructor: instructor.trim() } : {}),
    };
    create.mutate(
      { personnelId, input },
      {
        onSuccess: () => {
          toast.success('Qualification added.');
          onClose();
        },
        onError: (err) =>
          setFormError(err instanceof ApiError ? err.message : 'Failed to add qualification.'),
      },
    );
  };

  return (
    <CredentialModal
      title="Add Firearms Qualification"
      open={open}
      onClose={onClose}
      formId="firearms-form"
      busy={create.isPending}
    >
      <form id="firearms-form" onSubmit={onSubmit} className="form-grid">
        <FormField label="Weapon class">
          <select
            className="form-input"
            value={weaponClass}
            onChange={(e) => setWeaponClass(e.target.value as WeaponClass)}
          >
            {WEAPON_CLASS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="Score (0–100)">
          <Input
            type="number"
            min={0}
            max={100}
            value={score}
            onChange={(e) => setScore(e.target.value)}
          />
        </FormField>
        <FormField label="Qualified">
          <DateInput value={qualifiedAt} onChange={setQualifiedAt} />
        </FormField>
        <FormField label="Expires">
          <DateInput value={expiresAt} onChange={setExpiresAt} />
        </FormField>
        <FormField label="Instructor">
          <Input value={instructor} onChange={(e) => setInstructor(e.target.value)} />
        </FormField>
        {formError && (
          <p className="form-error" role="alert">
            {formError}
          </p>
        )}
      </form>
    </CredentialModal>
  );
}

// =============================================================================
// Clearances tab (SENSITIVE-ish: credential.read / credential.write)
// =============================================================================

function ClearancesTab({ personnelId }: { personnelId: number }): JSX.Element {
  const canWrite = can('credential.write');
  const { data, isLoading, isError } = useClearances(personnelId, can('credential.read'));
  const create = useCreateClearance();
  const del = useDeleteClearance();
  const [addOpen, setAddOpen] = useState(false);
  const [confirmId, setConfirmId] = useState<number | null>(null);

  const items = data?.clearances ?? [];

  return (
    <SectionFrame
      title="Clearances"
      canWrite={canWrite}
      onAdd={() => setAddOpen(true)}
      addLabel="Add Clearance"
      sensitive
    >
      {isLoading && <div className="page-state">Loading…</div>}
      {isError && <div className="page-state page-error">Failed to load clearances.</div>}
      {!isLoading && !isError && (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>Level</Table.HeaderCell>
              <Table.HeaderCell>Granted</Table.HeaderCell>
              <Table.HeaderCell>Expires</Table.HeaderCell>
              <Table.HeaderCell>Adjudicator</Table.HeaderCell>
              <Table.HeaderCell />
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {items.length === 0 && (
              <Table.Empty colSpan={5}>No clearances on file.</Table.Empty>
            )}
            {items.map((c: Clearance) => (
              <Table.Row key={c.id}>
                <Table.Cell>{labelFor(CLEARANCE_LEVEL_OPTIONS, c.clearance_level)}</Table.Cell>
                <Table.Cell>{fmtDate(c.granted_at)}</Table.Cell>
                <Table.Cell>
                  <ExpiryCell epoch={c.expires_at} />
                </Table.Cell>
                <Table.Cell>{c.adjudicator ?? '—'}</Table.Cell>
                <Table.Cell className="table-actions">
                  <RowActions canWrite={canWrite} onDelete={() => setConfirmId(c.id)} />
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      {addOpen && (
        <ClearanceFormModal
          personnelId={personnelId}
          open={addOpen}
          onClose={() => setAddOpen(false)}
          create={create}
        />
      )}

      <ConfirmDialog
        open={confirmId !== null}
        title="Remove clearance"
        message="This clearance will be removed from the personnel record."
        confirmLabel="Remove"
        danger
        busy={del.isPending}
        onCancel={() => setConfirmId(null)}
        onConfirm={() => {
          if (confirmId == null) return;
          del.mutate(
            { personnelId, childId: confirmId },
            {
              onSuccess: () => {
                toast.success('Clearance removed.');
                setConfirmId(null);
              },
              onError: () => toast.danger('Failed to remove clearance.'),
            },
          );
        }}
      />
    </SectionFrame>
  );
}

function ClearanceFormModal({
  personnelId,
  open,
  onClose,
  create,
}: {
  personnelId: number;
  open: boolean;
  onClose: () => void;
  create: ReturnType<typeof useCreateClearance>;
}): JSX.Element {
  const [level, setLevel] = useState<ClearanceLevel>('basic_bgc');
  const [grantedAt, setGrantedAt] = useState<number | null>(null);
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [adjudicator, setAdjudicator] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    setFormError(null);
    const input: CreateClearanceInput = {
      clearance_level: level,
      ...(grantedAt != null ? { granted_at: grantedAt } : {}),
      ...(expiresAt != null ? { expires_at: expiresAt } : {}),
      ...(adjudicator.trim() ? { adjudicator: adjudicator.trim() } : {}),
    };
    create.mutate(
      { personnelId, input },
      {
        onSuccess: () => {
          toast.success('Clearance added.');
          onClose();
        },
        onError: (err) =>
          setFormError(err instanceof ApiError ? err.message : 'Failed to add clearance.'),
      },
    );
  };

  return (
    <CredentialModal
      title="Add Clearance"
      open={open}
      onClose={onClose}
      formId="clearance-form"
      busy={create.isPending}
    >
      <form id="clearance-form" onSubmit={onSubmit} className="form-grid">
        <FormField label="Level">
          <select
            className="form-input"
            value={level}
            onChange={(e) => setLevel(e.target.value as ClearanceLevel)}
          >
            {CLEARANCE_LEVEL_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="Granted">
          <DateInput value={grantedAt} onChange={setGrantedAt} />
        </FormField>
        <FormField label="Expires">
          <DateInput value={expiresAt} onChange={setExpiresAt} />
        </FormField>
        <FormField label="Adjudicator">
          <Input value={adjudicator} onChange={(e) => setAdjudicator(e.target.value)} />
        </FormField>
        {formError && (
          <p className="form-error" role="alert">
            {formError}
          </p>
        )}
      </form>
    </CredentialModal>
  );
}

// =============================================================================
// Trainings tab (credential.read / credential.write)
// =============================================================================

function TrainingsTab({ personnelId }: { personnelId: number }): JSX.Element {
  const canWrite = can('credential.write');
  const { data, isLoading, isError } = useTrainings(personnelId, can('credential.read'));
  const create = useCreateTraining();
  const del = useDeleteTraining();
  const [addOpen, setAddOpen] = useState(false);
  const [confirmId, setConfirmId] = useState<number | null>(null);

  const items = data?.trainings ?? [];

  return (
    <SectionFrame
      title="Training"
      canWrite={canWrite}
      onAdd={() => setAddOpen(true)}
      addLabel="Add Training"
    >
      {isLoading && <div className="page-state">Loading…</div>}
      {isError && <div className="page-state page-error">Failed to load training.</div>}
      {!isLoading && !isError && (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>Course</Table.HeaderCell>
              <Table.HeaderCell>Hours</Table.HeaderCell>
              <Table.HeaderCell>Completed</Table.HeaderCell>
              <Table.HeaderCell>Expires</Table.HeaderCell>
              <Table.HeaderCell />
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {items.length === 0 && (
              <Table.Empty colSpan={5}>No training records on file.</Table.Empty>
            )}
            {items.map((t: Training) => (
              <Table.Row key={t.id}>
                <Table.Cell>{t.course}</Table.Cell>
                <Table.Cell>{t.hours ?? '—'}</Table.Cell>
                <Table.Cell>{fmtDate(t.completed_at)}</Table.Cell>
                <Table.Cell>
                  <ExpiryCell epoch={t.expires_at} />
                </Table.Cell>
                <Table.Cell className="table-actions">
                  <RowActions canWrite={canWrite} onDelete={() => setConfirmId(t.id)} />
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      {addOpen && (
        <TrainingFormModal
          personnelId={personnelId}
          open={addOpen}
          onClose={() => setAddOpen(false)}
          create={create}
        />
      )}

      <ConfirmDialog
        open={confirmId !== null}
        title="Remove training"
        message="This training record will be removed from the personnel record."
        confirmLabel="Remove"
        danger
        busy={del.isPending}
        onCancel={() => setConfirmId(null)}
        onConfirm={() => {
          if (confirmId == null) return;
          del.mutate(
            { personnelId, childId: confirmId },
            {
              onSuccess: () => {
                toast.success('Training removed.');
                setConfirmId(null);
              },
              onError: () => toast.danger('Failed to remove training.'),
            },
          );
        }}
      />
    </SectionFrame>
  );
}

function TrainingFormModal({
  personnelId,
  open,
  onClose,
  create,
}: {
  personnelId: number;
  open: boolean;
  onClose: () => void;
  create: ReturnType<typeof useCreateTraining>;
}): JSX.Element {
  const [course, setCourse] = useState('');
  const [hours, setHours] = useState('');
  const [completedAt, setCompletedAt] = useState<number | null>(null);
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    setFormError(null);
    if (!course.trim()) {
      setFormError('Course is required.');
      return;
    }
    if (hours.trim() && !/^\d{1,4}(\.\d{1,2})?$/.test(hours.trim())) {
      setFormError('Hours must be a number (up to two decimals).');
      return;
    }
    const input: CreateTrainingInput = {
      course: course.trim(),
      ...(hours.trim() ? { hours: hours.trim() } : {}),
      ...(completedAt != null ? { completed_at: completedAt } : {}),
      ...(expiresAt != null ? { expires_at: expiresAt } : {}),
    };
    create.mutate(
      { personnelId, input },
      {
        onSuccess: () => {
          toast.success('Training added.');
          onClose();
        },
        onError: (err) =>
          setFormError(err instanceof ApiError ? err.message : 'Failed to add training.'),
      },
    );
  };

  return (
    <CredentialModal
      title="Add Training"
      open={open}
      onClose={onClose}
      formId="training-form"
      busy={create.isPending}
    >
      <form id="training-form" onSubmit={onSubmit} className="form-grid">
        <FormField label="Course" required>
          <Input
            value={course}
            onChange={(e) => setCourse(e.target.value)}
            required
            autoFocus
          />
        </FormField>
        <FormField label="Hours">
          <Input
            value={hours}
            onChange={(e) => setHours(e.target.value)}
            inputMode="decimal"
            placeholder="e.g. 8 or 8.5"
          />
        </FormField>
        <FormField label="Completed">
          <DateInput value={completedAt} onChange={setCompletedAt} />
        </FormField>
        <FormField label="Expires">
          <DateInput value={expiresAt} onChange={setExpiresAt} />
        </FormField>
        {formError && (
          <p className="form-error" role="alert">
            {formError}
          </p>
        )}
      </form>
    </CredentialModal>
  );
}

// =============================================================================
// Availability tab (credential.read / credential.write)
// =============================================================================

function AvailabilityTab({ personnelId }: { personnelId: number }): JSX.Element {
  const canWrite = can('credential.write');
  const { data, isLoading, isError } = useAvailability(personnelId, can('credential.read'));
  const create = useCreateAvailability();
  const del = useDeleteAvailability();
  const [addOpen, setAddOpen] = useState(false);
  const [confirmId, setConfirmId] = useState<number | null>(null);

  const items = data?.availability ?? [];

  return (
    <SectionFrame
      title="Availability"
      canWrite={canWrite}
      onAdd={() => setAddOpen(true)}
      addLabel="Add Window"
    >
      {isLoading && <div className="page-state">Loading…</div>}
      {isError && <div className="page-state page-error">Failed to load availability.</div>}
      {!isLoading && !isError && (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>Recurrence</Table.HeaderCell>
              <Table.HeaderCell>Start</Table.HeaderCell>
              <Table.HeaderCell>End</Table.HeaderCell>
              <Table.HeaderCell>Valid from</Table.HeaderCell>
              <Table.HeaderCell>Valid to</Table.HeaderCell>
              <Table.HeaderCell />
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {items.length === 0 && (
              <Table.Empty colSpan={6}>No availability windows on file.</Table.Empty>
            )}
            {items.map((a: PersonnelAvailability) => (
              <Table.Row key={a.id}>
                <Table.Cell tone="mono">{a.rrule ?? '—'}</Table.Cell>
                <Table.Cell>{fmtMinutes(a.start_min)}</Table.Cell>
                <Table.Cell>{fmtMinutes(a.end_min)}</Table.Cell>
                <Table.Cell>{fmtDate(a.valid_from)}</Table.Cell>
                <Table.Cell>{fmtDate(a.valid_to)}</Table.Cell>
                <Table.Cell className="table-actions">
                  <RowActions canWrite={canWrite} onDelete={() => setConfirmId(a.id)} />
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      {addOpen && (
        <AvailabilityFormModal
          personnelId={personnelId}
          open={addOpen}
          onClose={() => setAddOpen(false)}
          create={create}
        />
      )}

      <ConfirmDialog
        open={confirmId !== null}
        title="Remove availability window"
        message="This availability window will be removed from the personnel record."
        confirmLabel="Remove"
        danger
        busy={del.isPending}
        onCancel={() => setConfirmId(null)}
        onConfirm={() => {
          if (confirmId == null) return;
          del.mutate(
            { personnelId, childId: confirmId },
            {
              onSuccess: () => {
                toast.success('Availability window removed.');
                setConfirmId(null);
              },
              onError: () => toast.danger('Failed to remove availability window.'),
            },
          );
        }}
      />
    </SectionFrame>
  );
}

// Parse an HH:MM time string into minutes-of-day (0..1440). Returns null on
// empty, undefined on a malformed value.
function parseTimeToMinutes(value: string): number | null | undefined {
  if (!value.trim()) return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!m) return undefined;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return undefined;
  return h * 60 + min;
}

function AvailabilityFormModal({
  personnelId,
  open,
  onClose,
  create,
}: {
  personnelId: number;
  open: boolean;
  onClose: () => void;
  create: ReturnType<typeof useCreateAvailability>;
}): JSX.Element {
  const [rrule, setRrule] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [validFrom, setValidFrom] = useState<number | null>(null);
  const [validTo, setValidTo] = useState<number | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    setFormError(null);

    const startMin = parseTimeToMinutes(start);
    const endMin = parseTimeToMinutes(end);
    if (startMin === undefined || endMin === undefined) {
      setFormError('Times must be in HH:MM 24-hour format.');
      return;
    }

    const input: CreatePersonnelAvailabilityInput = {
      ...(rrule.trim() ? { rrule: rrule.trim() } : {}),
      ...(startMin != null ? { start_min: startMin } : {}),
      ...(endMin != null ? { end_min: endMin } : {}),
      ...(validFrom != null ? { valid_from: validFrom } : {}),
      ...(validTo != null ? { valid_to: validTo } : {}),
    };
    create.mutate(
      { personnelId, input },
      {
        onSuccess: () => {
          toast.success('Availability window added.');
          onClose();
        },
        onError: (err) =>
          setFormError(err instanceof ApiError ? err.message : 'Failed to add availability.'),
      },
    );
  };

  return (
    <CredentialModal
      title="Add Availability Window"
      open={open}
      onClose={onClose}
      formId="availability-form"
      busy={create.isPending}
    >
      <form id="availability-form" onSubmit={onSubmit} className="form-grid">
        <FormField label="Recurrence (RRULE)" className="form-field-wide">
          <Input
            className="mono"
            value={rrule}
            onChange={(e) => setRrule(e.target.value)}
            placeholder="FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR"
          />
        </FormField>
        <FormField label="Start time">
          <Input
            type="time"
            value={start}
            onChange={(e) => setStart(e.target.value)}
          />
        </FormField>
        <FormField label="End time">
          <Input
            type="time"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
          />
        </FormField>
        <FormField label="Valid from">
          <DateInput value={validFrom} onChange={setValidFrom} />
        </FormField>
        <FormField label="Valid to">
          <DateInput value={validTo} onChange={setValidTo} />
        </FormField>
        {formError && (
          <p className="form-error" role="alert">
            {formError}
          </p>
        )}
      </form>
    </CredentialModal>
  );
}

// =============================================================================
// Shared modal shell for credential add forms
// =============================================================================

interface CredentialModalProps {
  title: string;
  open: boolean;
  onClose: () => void;
  formId: string;
  busy: boolean;
  children: React.ReactNode;
}

function CredentialModal({
  title,
  open,
  onClose,
  formId,
  busy,
  children,
}: CredentialModalProps): JSX.Element {
  return (
    <Dialog
      title={title}
      open={open}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" form={formId} variant="primary" disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </>
      }
    >
      {children}
    </Dialog>
  );
}
